import { useEffect, useMemo, useRef, useState } from "react"

import {
  AdyenCheckout,
  ApplePay,
  Blik,
  Card,
  Dropin,
  GooglePay,
  Klarna,
  PayPal
} from "@adyen/adyen-web"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useLocation } from "react-router"

import {
  getAdyenErrorMessage,
  getSecuredFieldStyles,
  hideGooglePayIfNeeded,
  observeAdyenError
} from "./helpers"

import "./styles.less"
import "@adyen/adyen-web/styles/adyen.css"

const appEnvironment = process.env.REACT_APP_ENVIRONMENT

export function AdyenDropIn({
  session,
  clientKey,
  environment = "test",
  countryCode = "NL",
  locale = "en-US",
  onSuccess,
  onError
}) {
  const { pathname } = useLocation()
  const containerRef = useRef(null)
  const [reloadLoop, setReloadLoop] = useState(false)
  const { t } = useTranslation()
  const theme = useSelector((state) => state.nav.theme)
  const isShowCards = !(pathname.includes("add-card") || pathname.includes("card-info"))
  const paymentMethodComponents = isShowCards
    ? [Card, PayPal, GooglePay, ApplePay, Klarna, Blik]
    : [Card]
  const isTest = appEnvironment !== "prod" && appEnvironment !== "staging"
  const normalizedSession = useMemo(() => {
    if (!session?.id || !session?.session) return null

    return {
      id: session.id,
      sessionData: session.session
    }
  }, [session])
  const securedFieldStyles = useMemo(() => getSecuredFieldStyles(theme), [theme])

  const handleError = (err) => {
    setTimeout(() => {
      setReloadLoop((p) => !p)
    }, 4000)

    observeAdyenError(getAdyenErrorMessage(err.resultCode))
    onError?.(err)
  }

  useEffect(() => {
    if (!containerRef.current || !normalizedSession) return

    let dropin
    let cleanupObserver

    ;(async () => {
      try {
        containerRef.current.innerHTML = ""

        const checkout = await AdyenCheckout({
          environment,
          clientKey,
          countryCode,
          locale,
          session: normalizedSession,

          onPaymentCompleted: (result) => onSuccess?.(result),
          onPaymentFailed: (e) => handleError(e),
          onError: (e) => handleError(e)
        })

        dropin = new Dropin(checkout, {
          paymentMethodComponents,
          showStoredPaymentMethods: isShowCards,
          paymentMethodsConfiguration: {
            card: {
              hasHolderName: isTest,
              holderNameRequired: isTest,
              positionHolderNameOnTop: isTest,
              styles: securedFieldStyles
            },
            storedCard: {
              styles: securedFieldStyles
            }
          }
        })

        dropin.mount(containerRef.current)
        cleanupObserver = hideGooglePayIfNeeded(isShowCards)
      } catch (e) {
        console.error("Adyen init error:", e)
        onError?.(e)
      }
    })()

    return () => {
      try {
        dropin?.unmount?.()
        cleanupObserver?.()
      } catch {}
    }
  }, [
    normalizedSession?.id,
    normalizedSession?.sessionData,
    clientKey,
    environment,
    countryCode,
    locale,
    reloadLoop,
    isShowCards,
    isTest,
    theme
  ])

  return <div ref={containerRef} className="adyen-dropin" />
}
